'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from '@/lib/router';
import { useTranslation } from 'react-i18next';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import { useLocationProducts } from '@/redux/back-dashboard-api/hooks';
import { LoadingFullScreen } from '@/components/helpers/loading';
import { GenericErrorCard } from '@/components/data/error';
import { ProductWithStock } from '@/lib/utils/database';
import { ItemDocuments, ItemProperties } from '@/components/data/item';
import { useModal } from '@pcmp/react-providers';
import { Location, OrderType } from '@/redux/back-dashboard-api';
import { PageContainer } from '@/components/layouts/page-admin';
import {
  InnerPageContainer,
  PageContainerHeader,
} from '@/components/layouts/page-webshop';
import { PageBreadcrumbs } from '@/components/layouts/page-breadcrumbs';
import HomeIcon from '@ingka/ssr-icon/paths/home';
import { OrderOnBehalfButton } from '@/components/buttons/order-on-behalf-button';
import { ColorPicker } from '@/components/forms/color-picker';
import { ItemCategoryTextBadge } from '@/components/badges/item-category';
import { useWebshopMode } from '@/components/page-widgets/webshop/context';
import { ItemImageCarousel } from '@/components/containers/item-image-carousel';
import { WardrobeBagButton } from '@/components/buttons/wardrobe-bag-button';
import {
  ItemStockStatus,
  getTotalStock,
} from '@/components/badges/item-stock-status';
import { AddProductToBagButton } from '@/components/buttons/add-product-to-bag-button';
import { ItemDescription } from '@/components/containers/item-description';

const ItemDetail: FC<{
  location: Location;
  product: ProductWithStock;
  item: ProductWithStock['items'][number];
}> = ({ item, product }) => {
  // Hooks
  const { t } = useTranslation();
  const { openModal } = useModal();
  const [selectedColor, setSelectedColor] = useState(
    item.combinations.color || undefined
  );
  const { orderType, showStock, allowOutOfStockOrders, updateItem } =
    useWebshopMode();
  // Data
  const totalStock = getTotalStock(product);
  const preferredItem = useMemo(() => {
    const candidates = product.items.filter((itm) =>
      selectedColor ? itm.combinations.color === selectedColor : true
    );

    return candidates[0];
  }, [product.items, selectedColor]);

  // Event handlers
  const handleConfirmDismiss = useCallback(() => {
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 250);
  }, []);

  const handleCombinationPicker = useCallback(async () => {
    const component = await import(
      '@/components/siders/product-combination'
    ).then((m) => m.default);

    openModal({
      component,
      props: {
        product,
        preferredItem,
        onSelect: (item, amount) => {
          updateItem({
            id: item._id,
            amount,
          });
        },
        onConfirmDismiss: handleConfirmDismiss,
        orderType,
      },
    });
  }, [
    openModal,
    product,
    preferredItem,
    orderType,
    updateItem,
    handleConfirmDismiss,
  ]);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-4 gap-x-8">
        <div className="w-full pb-4">
          <ItemImageCarousel item={preferredItem} />
        </div>

        <div className="w-full">
          <h3 className="text-2xl">{preferredItem.displayName}</h3>

          <span className="text-sm text-skapa-text-2">
            <ItemCategoryTextBadge category={item.category} />
          </span>

          <div className="mt-2">
            <ItemDescription description={preferredItem.description} />
          </div>

          <div className="mt-2">
            <span>{t('label.availableColors')}</span>

            <ColorPicker
              colors={product.combinations.color}
              selected={selectedColor}
              onChange={(color) => {
                setSelectedColor(color);
              }}
            />
          </div>
          {showStock && (
            <div className="mt-4">
              <ItemStockStatus stock={totalStock} />
            </div>
          )}
        </div>
      </div>

      <div className="w-full flex justify-end mb-8">
        <AddProductToBagButton
          text={t(
            orderType == OrderType.Order
              ? 'label.addToBag'
              : 'label.addToWardrobe'
          )}
          product={product}
          onAddToBag={handleCombinationPicker}
          allowOutOfStockOrders={allowOutOfStockOrders}
        />
      </div>

      <div className="w-full">
        <ItemDocuments documents={preferredItem.documents} />
        <ItemProperties properties={preferredItem.properties} />
      </div>
    </>
  );
};

const ItemWrapper: FC<{
  location: Location;
  itemSlug: string;
}> = ({ location, itemSlug }) => {
  const { showStock } = useWebshopMode();
  const {
    data: products,
    isLoading,
    error,
  } = useLocationProducts(location, { onlyInWarehouse: showStock });
  const product = useMemo(
    () =>
      products.find(({ items }) => items.find(({ slug }) => slug === itemSlug)),
    [products, itemSlug]
  );
  const item = useMemo(
    () => product?.items.find(({ slug }) => slug === itemSlug),
    [product, itemSlug]
  );

  if (isLoading) {
    return <LoadingFullScreen />;
  }

  if (error || !product || !item) {
    let currError = error;

    if (!product || !item) currError = new Error('Item not found');

    return <GenericErrorCard className="pt-6" error={currError} card />;
  }

  return <ItemDetail location={location} product={product} item={item} />;
};

export function ItemPage({
  itemId,
  breadcrumbLabel,
}: {
  itemId: string;
  breadcrumbLabel: string;
}) {
  // Hooks
  const router = useRouter();
  const { getShopPath, orderType } = useWebshopMode();
  // Data
  const { data: location, isLoading, error } = useActiveLocation();

  const breadcrumbs = useMemo(
    () => [
      {
        icon: HomeIcon,
        label: breadcrumbLabel,
        path: location ? getShopPath(location._id) : undefined,
      },
      {
        label: itemId,
      },
    ],
    [location, itemId, breadcrumbLabel, getShopPath]
  );

  // Validate shopping bag
  useEffect(() => {
    if (!location && !isLoading) {
      router.push(getShopPath() as any);
    }
  }, [location, isLoading, error, router, getShopPath]);

  if (isLoading) {
    return <LoadingFullScreen />;
  }

  if (error || !location)
    return <GenericErrorCard className="pt-6" error={error} card />;

  return (
    <PageContainer id="shopPage" disablePadding>
      <PageContainerHeader>
        <div className="flex flex-row justify-between items-center flex-wrap w-full gap-4 md:gap-0">
          <div className="w-full  md:max-w-[500px]">
            <PageBreadcrumbs hasBackButton breadcrumbs={breadcrumbs} />
          </div>

          {orderType === OrderType.Inventory ? (
            <WardrobeBagButton />
          ) : (
            <OrderOnBehalfButton />
          )}
        </div>
      </PageContainerHeader>

      <InnerPageContainer>
        <ItemWrapper location={location} itemSlug={itemId} />
      </InnerPageContainer>
    </PageContainer>
  );
}
